import type {
  ApprovalAction,
  ApproveRequestResult,
  CommandEnvelope,
  CommandKind,
  CommandPayloads,
  MeContext,
  RemoteApprovals,
  RemoteStatusResult,
  RequestApprovalResult,
} from '@didto/domain';
import { buildCommand } from '@didto/domain';
import { PAUSED_REASON, currentConnectivity } from './connectivity';
import type { Json } from './database.types';
import { env } from './env';
import { isNetworkError } from './errors';
import { supabase } from './supabase';
import { useDeviceStore } from './deviceStore';

/**
 * Typed wrappers around the RPC surface. Every write goes through runCommand(); reads use
 * PostgREST selects guarded by RLS. The device session token (staff PIN session) is attached
 * automatically when present.
 */
export type CommandResult = Record<string, unknown> & { replayed: boolean };

export async function runCommand<K extends CommandKind>(
  kind: K,
  payload: CommandPayloads[K],
  options: { commandId?: string; locationId?: string } = {},
): Promise<CommandResult> {
  // ADR-037: nothing is started while the server cannot be reached. (What the outbox already holds
  // goes through sendCommand when the server answers again.)
  if (currentConnectivity() !== 'online') throw new Error(`network: ${PAUSED_REASON}`);
  const envelope: CommandEnvelope<K> = buildCommand(
    kind,
    payload,
    options.commandId ? { commandId: options.commandId } : {},
  );
  return sendCommand(envelope, options.locationId);
}

export async function sendCommand(
  envelope: CommandEnvelope,
  locationId?: string,
): Promise<CommandResult> {
  const token = useDeviceStore.getState().sessionToken;
  const call = () =>
    supabase.rpc('run_command', {
      p_command_id: envelope.command_id,
      p_kind: envelope.kind,
      p_payload: envelope.payload as unknown as Json,
      p_session_token: token ?? undefined,
      p_occurred_at: envelope.occurred_at,
      p_location_id: locationId ?? undefined,
      p_client_version: env.appVersion,
    });
  // A command is idempotent by its id, so a retry after a dropped connection can never apply twice.
  let { data, error } = await call();
  if (error && isNetworkError(error)) {
    await new Promise((r) => setTimeout(r, 800));
    ({ data, error } = await call());
  }
  if (error) throw error;
  return data as unknown as CommandResult;
}

export async function fetchMe(locationId?: string | null): Promise<MeContext> {
  const token = useDeviceStore.getState().sessionToken;
  const { data, error } = await supabase.rpc('me', {
    p_session_token: token ?? undefined,
    p_location_id: locationId ?? undefined,
  });
  if (error) throw error;
  return data as unknown as MeContext;
}

export interface OpenSessionResult {
  ok: boolean;
  code?: string;
  retry_after_seconds?: number;
  attempts_left?: number;
  session_token?: string;
  session?: {
    id: string;
    employee_id: string;
    employee_display_name: string;
    opened_at: string;
    expires_at: string;
    idle_lock_seconds: number;
  };
  permissions?: string[];
  landing?: string;
}

export async function openTerminalSession(
  employeeId: string,
  pin: string,
): Promise<OpenSessionResult> {
  const { data, error } = await supabase.rpc('open_terminal_session', {
    p_employee_id: employeeId,
    p_pin: pin,
  });
  if (error) throw error;
  return data as unknown as OpenSessionResult;
}

export async function closeTerminalSession(
  reason: 'manual' | 'idle' | 'logout' = 'manual',
): Promise<void> {
  const token = useDeviceStore.getState().sessionToken;
  if (!token) return;
  const { error } = await supabase.rpc('close_terminal_session', {
    p_session_token: token,
    p_reason: reason,
  });
  if (error) throw error;
}

/**
 * ADR-023: ask for an approval of one exact operation (the command kind and the payload it will
 * send). The server records what is asked, who may approve it and the PIN rule. A client request id
 * makes a retry after a dropped connection return the same request.
 */
export async function requestApproval(
  action: ApprovalAction,
  payload: Record<string, unknown>,
  requestId: string,
): Promise<RequestApprovalResult> {
  const token = useDeviceStore.getState().sessionToken;
  const { data, error } = await supabase.rpc('request_approval', {
    p_session_token: token ?? '',
    p_action: action,
    p_payload: payload as unknown as Json,
    p_request_id: requestId,
  });
  if (error) throw error;
  return data as unknown as RequestApprovalResult;
}

/**
 * The approver confirms with their PIN. The check is its own call that never raises on a wrong
 * PIN, so every failed attempt is recorded and counts toward the lockout; a correct PIN returns a
 * short-lived, single-use grant that the command then carries as `approval_grant_id`.
 */
export async function approveRequest(
  requestId: string,
  approverEmployeeId: string,
  pin: string,
): Promise<ApproveRequestResult> {
  const token = useDeviceStore.getState().sessionToken;
  const { data, error } = await supabase.rpc('approve_request', {
    p_session_token: token ?? '',
    p_request_id: requestId,
    p_approver_employee_id: approverEmployeeId,
    p_pin: pin,
  });
  if (error) throw error;
  return data as unknown as ApproveRequestResult;
}

/* ── the owner's phone (ADR-033) ── */

/** The tablet sends a request it made to the owner's phone; returns where it stands. */
export async function askRemoteApproval(requestId: string): Promise<RemoteStatusResult> {
  const token = useDeviceStore.getState().sessionToken;
  const { data, error } = await supabase.rpc('approval_ask_remote', {
    p_session_token: token ?? '',
    p_request_id: requestId,
  });
  if (error) throw error;
  return data as unknown as RemoteStatusResult;
}

/** Where a request of this tablet stands (the grant once given). Never keeps the PIN session awake. */
export async function remoteApprovalStatus(requestId: string): Promise<RemoteStatusResult> {
  const token = useDeviceStore.getState().sessionToken;
  const { data, error } = await supabase.rpc('approval_remote_status', {
    p_session_token: token ?? '',
    p_request_id: requestId,
  });
  if (error) throw error;
  return data as unknown as RemoteStatusResult;
}

/** The tablet takes back a request still waiting on the phone. */
export async function withdrawRemoteApproval(requestId: string): Promise<RemoteStatusResult> {
  const token = useDeviceStore.getState().sessionToken;
  const { data, error } = await supabase.rpc('approval_withdraw_remote', {
    p_session_token: token ?? '',
    p_request_id: requestId,
  });
  if (error) throw error;
  return data as unknown as RemoteStatusResult;
}

/** What waits on the owner's phone, and what was decided over the last day. */
export async function fetchRemoteApprovals(): Promise<RemoteApprovals> {
  const { data, error } = await supabase.rpc('remote_approvals');
  if (error) throw error;
  return data as unknown as RemoteApprovals;
}

export async function terminalHeartbeat(state: Record<string, unknown> = {}) {
  const { data, error } = await supabase.rpc('terminal_heartbeat', {
    p_app_version: env.appVersion,
    p_state: state as never,
  });
  if (error) throw error;
  return data as {
    ok: boolean;
    status: string;
    revoked: boolean;
    server_time: string;
    heartbeat_seconds: number;
  };
}

export interface StationHealth {
  station_id: string;
  code: string;
  name: string;
  terminals: number;
  last_seen_at: string | null;
  online: boolean;
  threshold_seconds: number;
  server_time: string;
}

/** KDS screens per station at the caller's location (a device) or the given one (a human). */
export async function fetchStationHealth(locationId?: string): Promise<StationHealth[]> {
  const { data, error } = await supabase.rpc('station_health', {
    ...(locationId ? { p_location_id: locationId } : {}),
  });
  if (error) throw error;
  return (data ?? []) as unknown as StationHealth[];
}

export async function health() {
  const { data, error } = await supabase.rpc('health');
  if (error) throw error;
  return data as unknown as {
    ok: boolean;
    service: string;
    db_time: string;
    schema_version: string | null;
  };
}

/** Exchange a one-time enrollment code for a device session (terminal-enroll Edge Function). */
export async function enrollTerminal(code: string): Promise<{
  ok: boolean;
  code?: string;
  terminal?: {
    id: string;
    name: string;
    code: string;
    kind: string;
    location_id: string;
    landing: string;
  };
  session?: { access_token: string; refresh_token: string };
}> {
  const res = await fetch(`${env.supabaseUrl}/functions/v1/terminal-enroll`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: env.supabaseKey,
      Authorization: `Bearer ${env.supabaseKey}`,
    },
    body: JSON.stringify({ code, app_version: env.appVersion }),
  });
  return (await res.json()) as never;
}
